import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { GetAllUnidadesGerencias } from "@services/UnidadeGerencia/Index";
import SweetAlert from "@components/Feedback/SweetAlert/Index";
import { useFetchPaginatedData } from "@hooks/api/useFetchPaginatedData";

export const useFetchDataUnidades = () => {
  const [unitLoading, setUnitLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);

  const isMountedRef = useRef(true);

  const {
    data: allUnits,
    loading: paginatedUnitLoading,
    fetchAllData: fetchAllUnits,
  } = useFetchPaginatedData(GetAllUnidadesGerencias);

  useEffect(() => {
    isMountedRef.current = true;
    fetchAllUnits();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoiza as opções para evitar recomputação
  const mappedUnitOptions = useMemo(() => {
    if (allUnits.length === 0) return [];
    return allUnits
      .map((item) => ({
        value: item.id,
        label: item.nome,
        nomeGerente: item.nomeGerente,
        nomeEncarregado: item.nomeEncarregado,
        sigla: item.sigla,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allUnits]);

  // Atualiza unitOptions apenas quando realmente necessário
  useEffect(() => {
    if (isMountedRef.current) {
      setUnitOptions(mappedUnitOptions);
    }
  }, [mappedUnitOptions]);

  // Busca uma unidade por nome
  const fetchUnits = useCallback(
    async (nomeUnidadeGerencia) => {
      setUnitLoading(true);

      try {
        if (!nomeUnidadeGerencia) return null;

        const unidade = allUnits.find(
          (item) =>
            item.nome?.toLowerCase().trim() ===
            nomeUnidadeGerencia.toLowerCase().trim()
        );

        if (!unidade) {
          SweetAlert.error(
            "Unidade/Gerência não encontrada",
            "Verifique o nome e tente novamente."
          );
        }

        return unidade || null;
      } catch (error) {
        console.error("Erro ao buscar unidade/gerência:", error);
        return null;
      } finally {
        if (isMountedRef.current) setUnitLoading(false);
      }
    },
    [allUnits]
  );

  return {
    fetchUnits,
    unitOptions,
    setUnitOptions,
    unitLoading: unitLoading || paginatedUnitLoading,
  };
};
